import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../lib/api'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getLanguageByCode, getLanguageFlag } from '../../lib/constants/languages'
import BookCard from '../BookCard'
import { BookGenre } from '../../types'

interface TakenBook {
  id: string
  title_international: string
  author_international: string
  cover_url?: string | null
  original_language?: string | null
  format?: string | null
  genre?: string | null
  translation_id: string
  translation_language: string
  cefr_level?: string | null
  taken_at: string
  reading_progress: number
  estimated_pages?: number | null
}

interface TakenBooksSectionProps {
  onBooksLoaded?: (count: number) => void
}

// Language and level badges row
function CoverBadges({ language, level }: { language: string; level?: string | null }) {
  return (
    <div className="cover-badges flex items-center gap-1">
      <span className="language-flag text-sm" title={language}>
        {getLanguageFlag(language)}
      </span>
      {level && (
        <span className="cefr-badge bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded font-medium">
          {level}
        </span>
      )}
    </div>
  )
}

// Reading progress bar
function ProgressBar({ progress }: { progress: number }) {
  return (
    <div className="progress-bar-container w-full h-1.5 bg-black/40 rounded-b-sm overflow-hidden">
      <div 
        className="progress-bar-fill h-full bg-primary transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  )
}

export function TakenBooksSection({ onBooksLoaded }: TakenBooksSectionProps) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [books, setBooks] = useState<TakenBook[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadBooks()
  }, [])

  const loadBooks = async () => {
    try {
      setLoading(true)
      const response = await apiClient.reader.getTakenBooks()
      setBooks(response.books || [])
      onBooksLoaded?.(response.count || 0)
    } catch (error) {
      console.error('Error loading taken books:', error)
    } finally {
      setLoading(false)
    }
  }

  const getLanguageDisplayName = (langCode: string): string => {
    const lang = getLanguageByCode(langCode)
    return lang ? lang.name : langCode.toUpperCase()
  }

  const formatPages = (pages?: number | null): string => {
    if (!pages) return ''
    return `${pages} ${t('dashboard.pages', 'p.')}`
  }

  const handleReadOnline = (book: TakenBook, e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/read/${book.id}/${book.translation_id}`)
  }

  if (loading) {
    return (
      <div className="taken-books-section mb-8">
        <div className="section-header flex items-center gap-3 mb-4">
          <div className="w-2 h-6 bg-secondary rounded-full"></div>
          <h2 className="text-xl font-semibold">{t('dashboard.takenBooks', 'Taken from Library')}</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  // Don't show section if no taken books
  if (books.length === 0) {
    return null
  }

  return (
    <div className="taken-books-section mb-8">
      <div className="section-header flex items-center gap-3 mb-4">
        <div className="w-2 h-6 bg-secondary rounded-full"></div>
        <div>
          <h2 className="text-xl font-semibold">{t('dashboard.takenBooks', 'Taken from Library')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('dashboard.takenBooksDescription', 'Books you\'ve read from the public library')}
          </p>
        </div>
      </div>

      <div className="taken-books-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
        {books.map((book) => (
          <BookCard
            key={`${book.id}-${book.translation_id}`}
            book={{
              id: book.id,
              title_international: book.title_international,
              author_international: book.author_international,
              cover_url: book.cover_url || undefined,
              available_languages: [book.translation_language],
              genre: (book.genre as BookGenre) || null,
              original_language: book.original_language || ''
            }}
            variant="library-book"
            onCardClick={() => navigate(`/library/books/${book.id}`, { 
              state: { 
                from: 'my-books',
                translationLanguage: book.translation_language,
                cefrLevel: book.cefr_level
              } 
            })}
            badges={
              <CoverBadges 
                language={book.translation_language} 
                level={book.cefr_level} 
              />
            }
            bottomOverlay={
              <ProgressBar progress={book.reading_progress} />
            }
            extraInfo={
              <div className="extra-info-row flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span>{getLanguageDisplayName(book.translation_language)}</span>
                {book.cefr_level && (
                  <>
                    <span className="text-muted-foreground/50">•</span>
                    <span>{book.cefr_level}</span>
                  </>
                )}
                {book.estimated_pages && (
                  <>
                    <span className="text-muted-foreground/50">•</span>
                    <span>{formatPages(book.estimated_pages)}</span>
                  </>
                )}
              </div>
            }
            onHoverActionClick={(e) => handleReadOnline(book, e)}
            hoverActionLabel={t('reader.read', 'Read')}
            showLanguageFlags={false}
            showGenre={false}
          />
        ))}
      </div>
    </div>
  )
}
